import React, { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";
import Connectws from "./chat/Connectws";

const SocketEvents = {
  receiveMessage: "receive-message",
  updateMessageListener: "updateMessageListener",
  addMessage: "add-message",
};

const ChatScreen = ({ chat, onNameClick, userID }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  const currentUserId = userID;

  useEffect(() => {
    socketRef.current = Connectws();

    // JOIN personal room
    socketRef.current.emit(SocketEvents.join, currentUserId);

    // USER ONLINE
    socketRef.current.emit("user-online", currentUserId);

    // Listen for new messages
    socketRef.current.on(SocketEvents.receiveMessage, (msg) => {
      console.log("📥 receive-message:", msg);

      // Include messages where current user is sender OR receiver
      const isRelevant =
        msg.sender_id === currentUserId ||
        msg.reciever_id === currentUserId ||
        msg.sender_id === chat?.id ||
        msg.reciever_id === chat?.id;

      if (!isRelevant) return;

      setMessages((prev) => [
        ...prev,
        {
          sender_id: msg.sender_id === currentUserId ? "me" : msg.sender_id,
          message:
            msg.is_deleted || msg.is_delete_for_all
              ? "[Deleted message]"
              : msg.message ??
                (msg.media_url ? "[Media file]" : "[No message]"),
          time: msg.created_at,
          sender: msg.sender,
        },
      ]);
    });

    // Listen for message updates
    socketRef.current.on(SocketEvents.updateMessageListener, (data) => {
      console.log("🔄 updateMessageListener:", data);
      // You can implement logic to update message in `messages` if edited/deleted
    });

    // Listen for user online status
    socketRef.current.on("user-status", (data) => {
      const parsed = typeof data === "string" ? JSON.parse(data) : data;
      if (parsed.userId === chat?.id) setIsOnline(parsed.online);
    });

    return () => {
      if (socketRef.current) socketRef.current.disconnect();
    };
  }, [chat?.id]);

  // Send message
  const handleSend = () => {
    if (!input.trim()) return;

    const payload = {
      is_group_chat: false,
      isTyping: false,
      reciever_id: chat?.id,
      message: input,
      token: sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken"),
    };

    socketRef.current.emit(
      SocketEvents.addMessage,
      JSON.stringify(payload),
      (res) => {
        console.log("📩 Server response:", res);
        if (res?.success) {
          setMessages((prev) => [
            ...prev,
            { sender_id: "me", message: input, time: new Date().toISOString() },
          ]);
          setInput("");
        }
      }
    );
  };

  // Typing
  const handleTyping = (e) => {
    setInput(e.target.value);

    const typingPayload = {
      is_group_chat: false,
      isTyping: true,
      reciever_id: chat?.id,
      token: sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken"),
    };

    socketRef.current.emit(
      SocketEvents.addMessage,
      JSON.stringify(typingPayload)
    );
    setIsTyping(true);

    clearTimeout(window.typingTimeout);
    window.typingTimeout = setTimeout(() => {
      setIsTyping(false);
      socketRef.current.emit(
        SocketEvents.addMessage,
        JSON.stringify({ ...typingPayload, isTyping: false })
      );
    }, 1500);
  };

  return (
    <div className="flex flex-col h-[70vh] px-0">
      {/* Header */}
      <div className="flex items-center justify-between bg-green-50 rounded-xl p-3 shadow-sm">
        <div className="flex items-center space-x-3">
          <img
            src={chat?.avatar || "https://via.placeholder.com/40"}
            alt="profile"
            className="w-12 h-12 rounded-full border"
          />
          <div>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onNameClick();
              }}
            >
              <h2 className="font-semibold text-gray-800 hover:underline cursor-pointer">
                {chat?.name || "Alex Linderson"}
              </h2>
            </a>
            <p
              className={`text-sm flex items-center ${
                isOnline ? "text-green-600" : "text-gray-500"
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full mr-1 ${
                  isOnline ? "bg-green-600" : "bg-gray-400"
                }`}
              ></span>
              {isOnline ? "Online" : "Offline"}
            </p>
          </div>
        </div>

        <div className="relative">
          <input
            type="text"
            placeholder="Find Message"
            className="pl-9 pr-3 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400 text-sm"
          />
          <Search className="absolute left-2 top-2.5 text-gray-400 w-4 h-4" />
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {messages.map((msg, i) =>
          msg.sender_id === "me" ? (
            <div key={i} className="flex justify-end">
              <div className="bg-green-100 text-gray-800 px-4 py-2 rounded-2xl rounded-br-none max-w-xs">
                {msg.message}
                <div className="text-[10px] text-gray-500 text-right mt-1">
                  {msg.time ? new Date(msg.time).toLocaleTimeString() : "Now"}
                </div>
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-start space-x-2">
              <img
                src={
                  msg.sender?.profile_picture_url ||
                  chat?.avatar ||
                  "/default-avatar.png"
                }
                alt="user"
                className="w-8 h-8 rounded-full"
              />
              <div className="bg-gray-100 text-gray-800 px-4 py-2 rounded-2xl rounded-tl-none max-w-xs">
                {msg.message}
                <div className="text-[10px] text-gray-500 mt-1">
                  {msg.time ? new Date(msg.time).toLocaleTimeString() : "Now"}
                </div>
              </div>
            </div>
          )
        )}

        {isTyping && (
          <div className="text-sm text-gray-500 italic pl-3">
            {chat?.name || "User"} is typing...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex items-center border-t border-gray-300 py-2 px-3">
        <input
          type="text"
          value={input}
          onChange={handleTyping}
          placeholder="Add a comment"
          className="flex-1 px-3 py-2 border rounded-full focus:outline-none focus:ring-1 focus:ring-green-400"
        />
        <button
          onClick={handleSend}
          className="ml-2 px-4 py-2 bg-green-500 text-white rounded-full hover:bg-green-600"
        >
          Send
        </button>
      </div>
    </div>
  );
};

export default ChatScreen;
